import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input } from 'components/common';
import {
    createProjectInformationType,
    getAdminProjectInformationTypeById,
    getAdminProjectInformationTypes,
    updateProjectInformationType,
} from 'apis/adminProjectInformationTypes';
import { normalizeUploadedImageUrl, uploadImage } from 'apis/uploadImage';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const getFileNameFromUrl = (url) => {
    if (!url) return '';
    const cleanUrl = url.split('?')[0];
    return decodeURIComponent(cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1));
};

const ProjectInformationTypesForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const iconInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState(0);
    const [errors, setErrors] = useState({});
    const [uploadingIcon, setUploadingIcon] = useState(false);
    const [formData, setFormData] = useState({
        code: '',
        iconURL: '',
        sortOrder: 0,
        isActive: true,
        translation: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: detail, error: detailError } = useAdminDetailQuery(
        'project-information-types',
        id,
        getAdminProjectInformationTypeById,
    );
    const createMutation = useAdminCreateMutation('project-information-types', createProjectInformationType);
    const updateMutation = useAdminUpdateMutation('project-information-types', updateProjectInformationType);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData((prev) => ({
                ...prev,
                translation: languages.map((language) => ({ languageId: language.id, name: '' })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (!id) {
            getAdminProjectInformationTypes({ pageIndex: 1, pageSize: 1000 })
                .then((response) => {
                    const items = Array.isArray(response?.data) ? response.data : response?.data?.items || [];
                    const maxSortOrder = items.reduce((max, item) => Math.max(max, Number(item.sortOrder) || 0), 0);
                    setFormData((prev) => ({ ...prev, sortOrder: maxSortOrder + 1 }));
                })
                .catch(() => {});
        }
    }, [id]);

    useEffect(() => {
        if (!detail) return;
        setFormData({
            code: detail.code || '',
            iconURL: normalizeUploadedImageUrl(detail.iconURL || detail.iconUrl || ''),
            sortOrder: detail.sortOrder || 0,
            isActive: detail.isActive ?? true,
            translation: detail.translation || detail.translations || [],
        });
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleTranslationChange = (languageId, value) => {
        setFormData((prev) => ({
            ...prev,
            translation: prev.translation.some((item) => Number(item.languageId) === Number(languageId))
                ? prev.translation.map((item) => Number(item.languageId) === Number(languageId) ? { ...item, name: value } : item)
                : [...prev.translation, { languageId, name: value }],
        }));
    };

    const handleIconUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setUploadingIcon(true);
        try {
            const iconUrl = await uploadImage(file, { folder: 'icons' });
            setFormData((prev) => ({ ...prev, iconURL: iconUrl }));
            setErrors((prev) => ({ ...prev, iconURL: '' }));
            toast.success('Upload icon thành công');
        } catch (error) {
            toast.error(error?.message || 'Upload icon thất bại');
        } finally {
            setUploadingIcon(false);
        }
    };

    const validate = () => {
        const nextErrors = {};
        if (!formData.code.trim()) nextErrors.code = 'Vui lòng nhập mã';
        if (!formData.iconURL.trim()) nextErrors.iconURL = 'Vui lòng upload icon';
        if (!formData.translation.some((item) => item.name?.trim())) {
            nextErrors.translation = 'Vui lòng nhập tên cho ít nhất một ngôn ngữ';
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            code: formData.code.trim(),
            iconURL: formData.iconURL.trim(),
            sortOrder: Number(formData.sortOrder) || 0,
            isActive: Boolean(formData.isActive),
            translation: formData.translation.map((item) => ({
                languageId: Number(item.languageId),
                name: item.name || '',
            })),
        };

        try {
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);
            if (res.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/project-information-types');
            } else if (res.messageError) {
                setErrors(res.messageError);
            } else {
                toast.error(res.message || 'Lưu thất bại');
            }
        } catch {
            toast.error('Lưu thất bại');
        }
    };

    const activeLanguage = languages[activeTab];
    const activeTranslation = formData.translation.find((item) => Number(item.languageId) === Number(activeLanguage?.id));

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật loại thông tin' : 'Thêm loại thông tin'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt loại thông tin</p>
                <div className="input-group card-upload-field">
                    <label htmlFor="projectInfoIcon">Icon <span className="required">*</span></label>
                    <input
                        ref={iconInputRef}
                        id="projectInfoIcon"
                        name="projectInfoIcon"
                        type="file"
                        accept="image/*,.svg"
                        onChange={handleIconUpload}
                        disabled={uploadingIcon}
                        className="card-upload-input"
                    />
                    <button
                        type="button"
                        className={`card-upload-box ${formData.iconURL ? 'has-image' : ''}`}
                        onClick={() => iconInputRef.current?.click()}
                        disabled={uploadingIcon}
                    >
                        {formData.iconURL ? <img src={formData.iconURL} alt="" /> : <span>{uploadingIcon ? 'Đang upload icon...' : 'Upload icon'}</span>}
                    </button>
                    {formData.iconURL && <span className="field-help">{getFileNameFromUrl(formData.iconURL)}</span>}
                    {errors.iconURL && <span className="error-message">{errors.iconURL}</span>}
                </div>
                <div className="form-row">
                    <Input label="Mã" name="code" value={formData.code} onChange={handleChange} error={errors.code} required placeholder="address, investment, scale..." />
                    <Input label="Thứ tự" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                </div>
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                <p className="form-section-title">Bản dịch</p>
                <div className="language-tabs">
                    {languages.map((language, index) => (
                        <button key={language.id} type="button" className={activeTab === index ? 'active' : ''} onClick={() => setActiveTab(index)}>
                            {language.name}
                        </button>
                    ))}
                </div>
                {activeLanguage && (
                    <Input
                        label={`Tên (${activeLanguage.name})`}
                        name={`name-${activeLanguage.id}`}
                        value={activeTranslation?.name || ''}
                        onChange={(e) => handleTranslationChange(activeLanguage.id, e.target.value)}
                        error={errors.translation}
                        required
                    />
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading || uploadingIcon}>{loading ? 'Đang lưu...' : 'Lưu'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/project-information-types')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default ProjectInformationTypesForm;
